<div class="p-6 space-y-6 max-w-7xl mx-auto">

  <!-- Header & Title Banner -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/70 backdrop-blur-md p-6 rounded-2xl border border-emerald-100 shadow-sm">
    <div>
      <div class="flex items-center gap-2">
        <h1 class="text-2xl font-bold text-slate-800">AI Knowledgebase & Training Dataset</h1>
        <span class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">Super Admin</span>
      </div>
      <p class="text-sm text-slate-500 mt-1">
        Manage empirical survey responses, curate gold-standard AI training pairs, and export datasets for JoyBot model tuning.
      </p>
    </div>
    
    <div class="flex items-center gap-3">
      <a href="http://localhost:8080/survey.html" target="_blank"
         class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition text-sm font-semibold shadow-sm">
        <span>Open Public Survey</span>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
        </svg>
      </a>

      <button (click)="exportDataset()"
              class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white transition text-sm font-semibold shadow-md shadow-emerald-600/20">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
        </svg>
        <span>Export AI Dataset (JSON)</span>
      </button>
    </div>
  </div>

  <!-- Summary Statistics Grid -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
    <!-- Total Respondents (Students who completed the survey) -->
    <div class="bg-white p-5 rounded-2xl border border-emerald-200/80 shadow-sm flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Total Respondents</p>
        <p class="text-2xl font-bold text-emerald-800 mt-1">{{ stats?.total_respondents || 0 }}</p>
        <p class="text-xs text-slate-500 mt-0.5">Students completed survey</p>
      </div>
      <div class="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-xl font-bold">
        🎓
      </div>
    </div>

    <!-- Total Entries -->
    <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Dataset Rows</p>
        <p class="text-2xl font-bold text-slate-800 mt-1">{{ stats?.total_entries || 0 }}</p>
        <p class="text-xs text-brand-600 font-medium mt-0.5">Q&A training samples</p>
      </div>
      <div class="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center text-xl">
        📚
      </div>
    </div>

    <!-- Training Ready Samples -->
    <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Training Ready</p>
        <p class="text-2xl font-bold text-teal-700 mt-1">{{ stats?.training_ready || 0 }}</p>
        <p class="text-xs text-slate-400 mt-0.5">Approved for Joy AI model</p>
      </div>
      <div class="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center text-xl">
        ⚡
      </div>
    </div>

    <!-- Active Categories -->
    <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Categories</p>
        <p class="text-2xl font-bold text-indigo-700 mt-1">{{ stats?.categories?.length || 0 }}</p>
        <p class="text-xs text-slate-400 mt-0.5">Academic, work & social</p>
      </div>
      <div class="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl">
        🗂️
      </div>
    </div>

    <!-- Student Categories Covered -->
    <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Student Groups</p>
        <p class="text-2xl font-bold text-purple-700 mt-1">3 Groups</p>
        <p class="text-xs text-slate-400 mt-0.5">Full-time, part-time & working</p>
      </div>
      <div class="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl">
        👥
      </div>
    </div>
  </div>

  <!-- Search & Filter Controls -->
  <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
    <div class="flex flex-col md:flex-row items-center gap-3">
      <!-- Search Input -->
      <div class="relative flex-1 w-full">
        <span class="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-400">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </span>
        <input type="text" [(ngModel)]="searchQuery" (keyup.enter)="onSearch()"
               placeholder="Search by user prompt, context keywords, tags, or intent..."
               class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 text-sm outline-none transition">
      </div>

      <!-- Search Trigger Button -->
      <button (click)="onSearch()"
              class="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold transition">
        Filter
      </button>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 pt-2 border-t border-slate-100 text-sm">
      <!-- Category Filter -->
      <div>
        <label class="block text-xs font-semibold text-slate-500 mb-1">Category</label>
        <select [(ngModel)]="selectedCategory" (change)="onFilterChange()"
                class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 text-sm bg-white outline-none">
          <option value="all">All Categories</option>
          <option value="academic_stress">Academic Stress (Students)</option>
          <option value="workplace_burnout">Workplace / Work-Study Burnout</option>
          <option value="loneliness_isolation">Loneliness & Isolation</option>
          <option value="imposter_syndrome">Imposter Syndrome</option>
          <option value="general_stress">General Stress / Late Night Vent</option>
          <option value="taboo_faq">Taboo Mental Health FAQs</option>
        </select>
      </div>

      <!-- Student Routine (Question 1) -->
      <div>
        <label class="block text-xs font-semibold text-slate-500 mb-1">Student Routine (Question 1)</label>
        <select [(ngModel)]="selectedDemographic" (change)="onFilterChange()"
                class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 text-sm bg-white outline-none">
          <option value="all">All University Students</option>
          <option value="full_time_student">1. University student (studying full-time)</option>
          <option value="student_part_time_job">2. Student who also works a part-time job</option>
          <option value="working_full_time_student">3. Working full-time while going to school</option>
        </select>
      </div>

      <!-- Geopolitical Region Filter -->
      <div>
        <label class="block text-xs font-semibold text-slate-500 mb-1">Geopolitical Region</label>
        <select [(ngModel)]="selectedRegion" (change)="onFilterChange()"
                class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 text-sm bg-white outline-none">
          <option value="all">All Regions</option>
          <option value="South West">South West</option>
          <option value="South South">South South</option>
          <option value="South East">South East</option>
          <option value="North Central">North Central</option>
          <option value="North West">North West</option>
          <option value="North East">North East</option>
          <option value="Online">Online / Distance</option>
        </select>
      </div>

      <!-- Sort & Group Filter -->
      <div>
        <label class="block text-xs font-semibold text-slate-500 mb-1">Sort & Group</label>
        <select [(ngModel)]="selectedSort" (change)="onFilterChange()"
                class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 text-sm bg-white outline-none">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="student_category">Group by Student Routine</option>
          <option value="severity">By Clinical Severity (PHQ/GAD)</option>
          <option value="category">By Topic Category</option>
        </select>
      </div>

      <!-- Status Filter -->
      <div>
        <label class="block text-xs font-semibold text-slate-500 mb-1">Training Status</label>
        <select [(ngModel)]="selectedStatus" (change)="onFilterChange()"
                class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 text-sm bg-white outline-none">
          <option value="all">All Statuses</option>
          <option value="1">Approved & Training Ready</option>
          <option value="0">Draft / Pending Review</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Knowledgebase Data Table -->
  <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="p-12 text-center text-slate-400">
      <svg class="animate-spin h-8 w-8 mx-auto text-emerald-600 mb-3" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
      </svg>
      <p class="text-sm font-medium">Loading knowledgebase dataset entries...</p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoading && entries.length === 0" class="p-12 text-center">
      <div class="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center text-2xl mx-auto mb-3">
        🔍
      </div>
      <h3 class="text-base font-bold text-slate-700">No Dataset Entries Found</h3>
      <p class="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
        Try adjusting your search terms or filters, or submit a new survey response to populate the knowledge base.
      </p>
    </div>

    <!-- Table -->
    <div *ngIf="!isLoading && entries.length > 0" class="overflow-x-auto">
      <table class="w-full text-left text-sm text-slate-600">
        <thead class="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <tr>
            <th class="px-5 py-4 w-16">ID</th>
            <th class="px-5 py-4 w-44">Category / Group</th>
            <th class="px-5 py-4">User Prompt & Context</th>
            <th class="px-5 py-4">Ideal AI Response</th>
            <th class="px-5 py-4 w-36">Training Status</th>
            <th class="px-5 py-4 w-28 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr *ngFor="let item of entries" class="hover:bg-slate-50/70 transition">
            <!-- ID -->
            <td class="px-5 py-4 font-mono text-xs text-slate-400 font-semibold">
              #{{ item.id }}
            </td>

            <!-- Category & Demographic -->
            <td class="px-5 py-4">
              <span [class]="getCategoryBadgeClass(item.category)"
                    class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border mb-1">
                {{ formatCategoryLabel(item.category) }}
              </span>
              <p class="text-xs text-slate-500 capitalize flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                {{ formatDemographicLabel(item.demographic_group) }}
              </p>
              <!-- University & Geopolitical Region Badges -->
              <div *ngIf="extractInstitution(item.context) || extractRegion(item.context)" class="mt-1 flex flex-wrap items-center gap-1">
                <span *ngIf="extractInstitution(item.context)" class="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 font-semibold px-2 py-0.5 rounded-md border border-blue-200" [title]="'University: ' + extractInstitution(item.context)">
                  🏛️ {{ extractInstitution(item.context).length > 20 ? (extractInstitution(item.context) | slice:0:18) + '...' : extractInstitution(item.context) }}
                </span>
                <span *ngIf="extractRegion(item.context)" class="inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-800 font-semibold px-2 py-0.5 rounded-md border border-amber-200" title="Geopolitical Region">
                  📍 {{ extractRegion(item.context) }}
                </span>
              </div>
              <span *ngIf="item.intent" class="inline-block text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded mt-1">
                {{ item.intent }}
              </span>
              <div *ngIf="item.severity_level" class="mt-1.5">
                <span class="inline-block text-[10px] bg-slate-900 text-slate-100 font-semibold px-2 py-0.5 rounded-md" [title]="item.severity_level">
                  <span *ngIf="item.gad7_score !== null && item.gad7_score !== undefined">GAD: {{ item.gad7_score }}/21</span>
                  <span *ngIf="item.phq9_score !== null && item.phq9_score !== undefined"> | PHQ: {{ item.phq9_score }}/27</span>
                </span>
              </div>
            </td>

            <!-- Prompt & Context -->
            <td class="px-5 py-4 max-w-md">
              <p class="text-slate-900 font-medium text-xs leading-relaxed line-clamp-2" [title]="item.prompt">
                "{{ item.prompt }}"
              </p>
              <p *ngIf="item.context" class="text-[11px] text-slate-400 mt-1 line-clamp-1 italic" [title]="item.context">
                Context: {{ item.context }}
              </p>
              <div *ngIf="item.tags" class="flex flex-wrap gap-1 mt-1.5">
                <span *ngFor="let tag of item.tags.split(',')" class="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  #{{ tag.trim() }}
                </span>
              </div>
            </td>

            <!-- Ideal AI Response -->
            <td class="px-5 py-4 max-w-md">
              <p class="text-xs text-slate-700 leading-relaxed line-clamp-3 bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100/60" [title]="item.ideal_response">
                {{ item.ideal_response }}
              </p>
              <p *ngIf="item.avoid_patterns" class="text-[10px] text-rose-500 mt-1 line-clamp-1" [title]="item.avoid_patterns">
                ⚠️ Avoid: {{ item.avoid_patterns }}
              </p>
            </td>

            <!-- Status Toggle -->
            <td class="px-5 py-4">
              <button (click)="toggleApproval(item)"
                      [class.bg-emerald-100]="item.is_approved"
                      [class.text-emerald-800]="item.is_approved"
                      [class.border-emerald-200]="item.is_approved"
                      [class.bg-slate-100]="!item.is_approved"
                      [class.text-slate-600]="!item.is_approved"
                      [class.border-slate-200]="!item.is_approved"
                      class="px-2.5 py-1 rounded-full text-xs font-semibold border flex items-center gap-1.5 transition hover:opacity-80">
                <span class="w-2 h-2 rounded-full" [class.bg-emerald-500]="item.is_approved" [class.bg-slate-400]="!item.is_approved"></span>
                <span>{{ item.is_approved ? 'Training Ready' : 'Disabled' }}</span>
              </button>
            </td>

            <!-- Actions -->
            <td class="px-5 py-4 text-right">
              <div class="flex items-center justify-end gap-1.5">
                <!-- Edit Button -->
                <button (click)="openEditModal(item)"
                        class="p-2 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                        title="Edit dataset row (Super Admin)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                  </svg>
                </button>

                <!-- Delete Button -->
                <button (click)="deleteEntry(item)"
                        class="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete entry">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Footer -->
    <div *ngIf="totalPages > 1" class="px-6 py-4 bg-slate-50/60 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
      <div>
        Showing Page <span class="font-bold text-slate-700">{{ currentPage }}</span> of <span class="font-bold text-slate-700">{{ totalPages }}</span> ({{ totalEntries }} total entries)
      </div>
      <div class="flex items-center gap-1">
        <button (click)="goToPage(currentPage - 1)" [disabled]="currentPage === 1"
                class="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-40 transition">
          Previous
        </button>
        <button (click)="goToPage(currentPage + 1)" [disabled]="currentPage === totalPages"
                class="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-40 transition">
          Next
        </button>
      </div>
    </div>
  </div>

  <!-- SUPER ADMIN EDIT MODAL -->
  <div *ngIf="isModalOpen && editingEntry" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
    <div class="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 shadow-2xl border border-slate-100">
      
      <!-- Modal Header -->
      <div class="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
        <div>
          <h2 class="text-xl font-bold text-slate-900">Edit Knowledgebase Dataset Entry</h2>
          <p class="text-xs text-slate-500">Super Admin Training Refinement (ID #{{ editingEntry.id }})</p>
        </div>
        <button (click)="closeModal()" class="text-slate-400 hover:text-slate-600 p-2 rounded-xl hover:bg-slate-100 transition">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Modal Body -->
      <form (submit)="$event.preventDefault(); saveEdit()" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Category -->
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">Category</label>
            <select [(ngModel)]="editingEntry.category" name="category"
                    class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none">
              <option value="academic_stress">academic_stress</option>
              <option value="workplace_burnout">workplace_burnout</option>
              <option value="loneliness_isolation">loneliness_isolation</option>
              <option value="imposter_syndrome">imposter_syndrome</option>
              <option value="general_stress">general_stress</option>
              <option value="taboo_faq">taboo_faq</option>
            </select>
          </div>

          <!-- Demographic Group -->
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">Demographic Group</label>
            <select [(ngModel)]="editingEntry.demographic_group" name="demographic_group"
                    class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none">
              <option value="university_student">university_student</option>
              <option value="young_working_adult">young_working_adult</option>
              <option value="both">both</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Intent -->
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">Intent Classification Tag</label>
            <input type="text" [(ngModel)]="editingEntry.intent" name="intent"
                   class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none text-xs font-mono">
          </div>

          <!-- Tags -->
          <div>
            <label class="block text-xs font-semibold text-slate-700 mb-1">Tags (Comma-separated)</label>
            <input type="text" [(ngModel)]="editingEntry.tags" name="tags"
                   class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none text-xs">
          </div>
        </div>

        <!-- User Prompt -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">User Prompt / Query (Raw Input)</label>
          <textarea [(ngModel)]="editingEntry.prompt" name="prompt" rows="3" required
                    class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none text-xs leading-relaxed"></textarea>
        </div>

        <!-- Clinical Screening Level -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">Standard Psychometric Screening Profile (PHQ-9 & GAD-7)</label>
          <input type="text" [(ngModel)]="editingEntry.severity_level" name="severity_level"
                 placeholder="e.g., PHQ-9: Moderately Severe Depression (16/27) | GAD-7: Moderate Anxiety (14/21)"
                 class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none text-xs font-semibold text-emerald-800 bg-emerald-50/20">
        </div>

        <!-- Context Metadata -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">Emotional Context / Symptoms</label>
          <textarea [(ngModel)]="editingEntry.context" name="context" rows="2"
                    class="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 outline-none text-xs leading-relaxed text-slate-600"></textarea>
        </div>

        <!-- Target Ideal Response -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">Target Ideal AI Response (Gold Standard)</label>
          <textarea [(ngModel)]="editingEntry.ideal_response" name="ideal_response" rows="4" required
                    class="w-full px-3 py-2 rounded-xl border border-emerald-300 focus:ring-2 focus:ring-emerald-200 outline-none text-xs leading-relaxed bg-emerald-50/30"></textarea>
        </div>

        <!-- Avoid Patterns -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 mb-1">Avoid Patterns / Anti-Patterns (What NOT to say)</label>
          <textarea [(ngModel)]="editingEntry.avoid_patterns" name="avoid_patterns" rows="2"
                    class="w-full px-3 py-2 rounded-xl border border-rose-200 focus:ring-2 focus:ring-rose-100 outline-none text-xs leading-relaxed text-rose-700 bg-rose-50/30"></textarea>
        </div>

        <!-- Toggles -->
        <div class="flex items-center gap-6 pt-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" [(ngModel)]="editingEntry.is_approved" name="is_approved" class="rounded text-emerald-600">
            <span class="text-xs font-semibold text-slate-700">Approved for Training</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" [(ngModel)]="editingEntry.training_ready" name="training_ready" class="rounded text-emerald-600">
            <span class="text-xs font-semibold text-slate-700">Mark Training Ready</span>
          </label>
        </div>

        <!-- Modal Footer Actions -->
        <div class="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
          <button type="button" (click)="closeModal()"
                  class="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold transition text-xs">
            Cancel
          </button>
          <button type="button" (click)="saveEdit()" [disabled]="isSaving"
                  class="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md transition text-xs flex items-center gap-2 disabled:opacity-50">
            <svg *ngIf="isSaving" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
            </svg>
            <span>Save Dataset Changes</span>
          </button>
        </div>
      </form>

    </div>
  </div>

</div>
